import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    Keyboard,
    ListRenderItemInfo,
    Platform,
    ScrollView,
    View,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Edge, SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';

import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';
import { RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { CloseIcon } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    ADD_PROFILE_SAVE_BUTTON,
    PROFILE_ADD_PROFILE_ERROR_CODE,
    PROFILE_ADD_PROFILE_ERROR_DESCRIPTION,
    PROFILE_ADD_PROFILE_ERROR_TITLE,
    PROFILE_AGE_RATING_ITEM,
    PROFILE_BACK_BUTTON,
    PROFILE_CLOSE_BUTTON,
    PROFILE_KIDS_NOT_ALLOWED_DESCRIPTION,
    PROFILE_KIDS_NOT_ALLOWED_TITLE,
    PROFILE_OK_BUTTON,
    PROFILE_SELECTION_DESCRIPTION,
    PROFILE_SELECTION_TITLE,
    PROFILE_TRY_AGAIN_BUTTON,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import {
    selectLayoutConfig,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import {
    addProfile,
    changeUserProfile,
    changeUserProfileAvatar,
    changeUserProfileRating,
    getProfilesList,
} from 'store/user/actions';
import { ParentalControlRating, Profile } from 'store/user/reducer.types';
import {
    selectActiveUserProfile,
    selectAddProfileErrorCode,
    selectAddProfileErrorStatus,
    selectAddProfileRequestStatus,
    selectProfileAvatar,
    selectProfileParentalRating,
    selectProfilesList,
} from 'store/user/selectors';
import { RequestStatus } from 'store/utils';

import { RegularStorage, StorageKeys } from 'services/mmkv';
import { RetryController } from 'services/opf-retry-mechanism';
import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import { COLOR } from 'utils/colors';
import useBackHandler from 'utils/hooks/useBackHandler.ts';
import useNoInternet from 'utils/hooks/useNoInternet';
import useSafeAreaEdges from 'utils/hooks/useSafeAreaEdges';
import { isPhone, isTabletOrWeb, isWeb } from 'utils/responsiveness';

import { MAX_PROFILES } from 'screens/Profiles';

import BottomSheetAccessibleList from 'components/BottomSheetAccessibleList';
import BottomSheetList from 'components/BottomSheetList';
import rowStyles from 'components/BottomSheetRowItem/styles';
import SvgButton from 'components/CustomButtons/SvgButton';
import DialogBody from 'components/Dialog/components/DialogBody';
import ErrorDialog from 'components/ErrorDialog';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import {
    adultProfileRating,
    kidProfileRating,
} from '../ProfileAgeRating/constants';
import AgeRowItem from '../components/AgeRowItem';
import ProfileForm from '../components/ProfileForm';
import {
    validateProfileName,
    validateProfileNameToExistingNames,
} from '../utils';
import styles from './styles';
import { setParentalRating } from './utils';

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams, SCREEN.ADD_PROFILE>;
    route: RouteProp<RouteNavigationParams, SCREEN.ADD_PROFILE>;
};

const {
    PROFILE: {
        PROFILE_ADD_CHANE_ERROR_TITLE,
        PROFILE_ADD_CHANE_ERROR_DESCRIPTION,
        BUTTON_SAVE,
        PROFILES_ADD_NEW_PROFILE_TITLE,
        PROFILES_ADD_NEW_PROFILE_DESCRIPTION,
        PROFILE_ERROR_TITLE_KIDS_NOT_ALLOWED_TITLE,
        PROFILE_ERROR_TITLE_KIDS_NOT_ALLOWED_BODY,
        PROFILE_AGE_RATING_BUTTON,
        PROFILE_KIDS_AGE_BUTTON,
    },
    TRY_AGAIN_BUTTON,
    BACK_BUTTON,
    OK_BUTTON,
    ACCESSIBILITY: {
        LOGIN: { ACCESSIBILITY_CLOSE_BUTTON },
    },
} = LANGUAGE_KEYS;

function AddProfilePage({
    navigation,
    route: {
        params: { isChildModeEnabled },
    },
}: Props) {
    const {
        primary,
        error_color: errorColor,
        background,
    } = useSelector(selectLayoutConfig);
    const addProfileErrorCode = useSelector(selectAddProfileErrorCode);
    const addProfileErrorStatus = useSelector(selectAddProfileErrorStatus);
    const addProfileStatus = useSelector(selectAddProfileRequestStatus);
    const profiles = useSelector(selectProfilesList);
    const selectedProfileId = useSelector(selectActiveUserProfile);
    const profileParentalRating = useSelector(selectProfileParentalRating);
    const profileAvatar = useSelector(selectProfileAvatar);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const dispatch = useDispatch();

    const [isChildProfile] = useState(
        typeof isChildModeEnabled === 'string'
            ? JSON.parse(isChildModeEnabled)
            : isChildModeEnabled,
    );
    const [data, setData] = useState(
        isChildProfile ? kidProfileRating : adultProfileRating,
    );
    const [isBottomSheetOpened, setIsBottomSheetOpened] = useState(false);
    const [newProfileNameErrorText, setNewProfileNameErrorText] = useState('');
    const [hasProfileListError, setHasProfileListError] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);

    const { t } = useTranslation();

    const bottomSheetRef = useRef<BottomSheetModalMethods>(undefined);

    const edges: Edge[] = useSafeAreaEdges();
    const { isInternetConnected } = useNoInternet();

    const newProfileNameRef = React.useRef('');

    useEffect(() => {
        // set default values
        dispatch(
            changeUserProfileRating(
                setParentalRating(profileParentalRating, isChildProfile),
            ),
        );

        return () => {
            dispatch(changeUserProfileAvatar(null));
        };
    }, []);

    const buttonStyle = useMemo(
        () => ({
            borderColor: primary,
        }),
        [primary],
    );

    const isSelectedChildProfile = useMemo(
        () =>
            !!profiles.find(({ _id }) => _id === selectedProfileId)
                ?.childProfile,
        [profiles, selectedProfileId],
    );

    const errorContent = useMemo(() => {
        if (isSelectedChildProfile) {
            return {
                title: PROFILE_ERROR_TITLE_KIDS_NOT_ALLOWED_TITLE,
                description: PROFILE_ERROR_TITLE_KIDS_NOT_ALLOWED_BODY,
                actionButtonText: undefined,
                actionButtonTestID: undefined,
                cancelButtonText: OK_BUTTON,
                cancelButtonTestID: PROFILE_OK_BUTTON,
                titleTestID: PROFILE_KIDS_NOT_ALLOWED_TITLE,
                descriptionTestID: PROFILE_KIDS_NOT_ALLOWED_DESCRIPTION,
            };
        }

        return {
            title: PROFILE_ADD_CHANE_ERROR_TITLE,
            description: PROFILE_ADD_CHANE_ERROR_DESCRIPTION,
            actionButtonText: TRY_AGAIN_BUTTON,
            actionButtonTestID: PROFILE_TRY_AGAIN_BUTTON,
            cancelButtonText: BACK_BUTTON,
            cancelButtonTestID: PROFILE_BACK_BUTTON,
            titleTestID: PROFILE_ADD_PROFILE_ERROR_TITLE,
            descriptionTestID: PROFILE_ADD_PROFILE_ERROR_DESCRIPTION,
        };
    }, [isSelectedChildProfile]);

    const handleCloseBottomSheet = useCallback(() => {
        bottomSheetRef.current?.dismiss();
        setIsBottomSheetOpened(false);
    }, []);

    const handleHardwareBackPress = useCallback(() => {
        if (isBottomSheetOpened) {
            handleCloseBottomSheet();
            return true;
        }
        return false;
    }, [handleCloseBottomSheet, isBottomSheetOpened]);

    useBackHandler(handleHardwareBackPress);

    const handleSetNewProfileNameToRef = useCallback((text: string) => {
        newProfileNameRef.current = text;
    }, []);

    const handleSetNewRating = useCallback(
        (item: {
            label: string;
            key: ParentalControlRating;
            isSelected: boolean;
        }) => {
            dispatch(
                changeUserProfileRating({
                    rating: item.key,
                    key: item.key,
                }),
            );
        },
        [isChildProfile],
    );

    const handleOpenAgeRating = useCallback(() => {
        if (isTabletOrWeb) {
            navigation.push(SCREEN.PROFILE_AGE_RATING, {
                isChildModeEnabled: isChildProfile,
                ageRating: setParentalRating(
                    profileParentalRating,
                    isChildModeEnabled,
                ),
            });
        } else {
            bottomSheetRef.current?.present();
            setIsBottomSheetOpened(true);
        }
    }, [navigation, isChildProfile, profileParentalRating]);

    const handleChooseRating = useCallback(
        (item: {
            label: string;
            key: ParentalControlRating;
            isSelected: boolean;
        }) => {
            handleSetNewRating(item);

            setData((prevData) =>
                prevData.map((prevItem) => ({
                    ...prevItem,
                    isSelected: prevItem.key === item.key,
                })),
            );

            handleCloseBottomSheet();
        },
        [handleCloseBottomSheet, handleSetNewRating],
    );

    const handleClose = useCallback(() => {
        if (navigation.canGoBack()) {
            navigation.goBack();
        } else {
            navigation.reset({ routes: [{ name: SCREEN.PROFILE_TYPE }] });
        }

        dispatch(changeUserProfileRating(null));
        dispatch(changeUserProfileAvatar(null));
    }, [navigation]);

    const handleGoToProfiles = useCallback(() => {
        if (navigation.canGoBack()) {
            navigation.pop(2);
        } else {
            navigation.reset({ routes: [{ name: SCREEN.PROFILES }] });
        }

        dispatch(changeUserProfileRating(null));
        dispatch(changeUserProfileAvatar(null));
    }, [navigation]);

    const handleShowErrorDialog = useCallback(() => {
        setShowErrorDialog(true);
    }, []);

    const handleHideErrorDialog = useCallback(() => {
        setShowErrorDialog(false);
    }, []);

    const handleInvalidProfilesList = useCallback(() => {
        setHasProfileListError(true);
        handleShowErrorDialog();
    }, []);

    const handleCloseErrorDialog = useCallback(() => {
        handleGoToProfiles();
        handleHideErrorDialog();
    }, [handleGoToProfiles, handleHideErrorDialog]);

    const handleAddProfileSuccess = useCallback(
        async (newProfileId: string) => {
            const retryChangeProfile = () =>
                new Promise<void>((resolve, reject) => {
                    dispatch(
                        changeUserProfile(newProfileId, {
                            onSuccess: () => {
                                resolve();
                            },
                            onFailure: reject,
                        }),
                    );
                });

            dispatch(
                changeUserProfile(newProfileId, {
                    onFailure(error) {
                        if (isErrorNeedsRetry(error)) {
                            RetryController.showVitalDialog({
                                actionButtonTitle: LANGUAGE_KEYS.RELOAD_BUTTON,
                                actionButtonOnPress: retryChangeProfile,
                            });
                            RetryController.registerRetryCallback(
                                retryChangeProfile,
                                {
                                    retryDuration: 10 * 60 * 1000,
                                    maxRetryCount: 10,
                                },
                            );
                            RetryController.executeRetry();
                        }
                    },
                }),
            );

            await RegularStorage.setItem(
                StorageKeys.FTI_PROFILES_SHOWN,
                JSON.stringify(true),
            );

            handleGoToProfiles();
        },
        [handleGoToProfiles],
    );

    const handleProfileListSuccess = useCallback(
        (profileData: Profile[]) => {
            const canCreateNewAccount = profileData.length < MAX_PROFILES;

            if (!canCreateNewAccount) {
                handleInvalidProfilesList();
            } else {
                const emptyNameError = validateProfileName(
                    newProfileNameRef.current.trim(),
                );
                const existingNameError = validateProfileNameToExistingNames(
                    newProfileNameRef.current,
                    profiles,
                );
                setNewProfileNameErrorText(emptyNameError || existingNameError);

                if (
                    emptyNameError === '' &&
                    existingNameError === '' &&
                    !hasProfileListError &&
                    addProfileStatus !== RequestStatus.PENDING &&
                    canCreateNewAccount
                ) {
                    dispatch(
                        addProfile(
                            {
                                name: newProfileNameRef.current,
                                childProfile: isChildProfile,
                                ratingsThreshold: setParentalRating(
                                    profileParentalRating,
                                    isChildProfile,
                                ).rating,
                                iconName: profileAvatar || 'default',
                            },
                            {
                                onSuccess: handleAddProfileSuccess,
                                onFailure: handleShowErrorDialog,
                            },
                        ),
                    );
                }
            }
        },
        [
            handleInvalidProfilesList,
            profiles,
            profileParentalRating,
            isChildProfile,
            profileAvatar,
            addProfileStatus,
            hasProfileListError,
        ],
    );

    const handleActionButtonPress = useCallback(() => {
        handleHideErrorDialog();

        dispatch(
            getProfilesList({
                onSuccess: handleProfileListSuccess,
                onFailure: handleInvalidProfilesList,
            }),
        );
    }, [
        handleHideErrorDialog,
        handleProfileListSuccess,
        handleInvalidProfilesList,
    ]);

    const renderBottomSheetRow = useCallback(
        ({
            item,
        }: ListRenderItemInfo<{
            label: string;
            key: ParentalControlRating;
            isSelected: boolean;
        }>) => {
            return (
                <AgeRowItem
                    isSelected={item.isSelected}
                    key={item.key}
                    label={t(item.label)}
                    onSelect={() => handleChooseRating(item)}
                    testID={`${PROFILE_AGE_RATING_ITEM}_${item.key}`}
                    backgroundColor={COLOR.primary}
                />
            );
        },
        [handleChooseRating, isChildProfile],
    );

    const handleEndEditing = useCallback(() => {
        newProfileNameErrorText &&
            setNewProfileNameErrorText(
                validateProfileName(newProfileNameRef.current),
            );
    }, [newProfileNameErrorText]);

    const handleAvatarPress = useCallback(() => {
        navigation.push(SCREEN.PROFILE_AVATARS, {
            isChildModeEnabled: isChildProfile,
        });
    }, [navigation, isChildProfile]);

    const renderDescriptionComponent = useCallback(
        () => (
            <View>
                <Text
                    category={ETextCategory.body1}
                    color={ETextColor.light_grey}
                    style={styles.description}
                    testID={PROFILE_SELECTION_DESCRIPTION}
                    center
                    {...(isWeb && { tabIndex: 0 })}
                >
                    {t(PROFILES_ADD_NEW_PROFILE_DESCRIPTION)}
                </Text>
                <ProfileForm
                    onPress={handleOpenAgeRating}
                    text={newProfileNameRef.current}
                    profileNameErrorText={newProfileNameErrorText}
                    errorColor={errorColor}
                    isChildModeEnabled={isChildProfile}
                    onEndEditing={handleEndEditing}
                    onChangeText={handleSetNewProfileNameToRef}
                    onAvatarPress={handleAvatarPress}
                />
            </View>
        ),
        [
            newProfileNameErrorText,
            errorColor,
            handleOpenAgeRating,
            isChildProfile,
            handleEndEditing,
            handleSetNewProfileNameToRef,
            handleAvatarPress,
        ],
    );

    return (
        <SafeAreaView style={styles.container} edges={edges}>
            <ScrollView
                contentContainerStyle={styles.contentContainer}
                scrollEnabled={!isWeb && Keyboard.isVisible()}
            >
                <KeyboardAwareScrollView
                    contentContainerStyle={styles.keyboardAwareContainer}
                    scrollEnabled={!isWeb && Keyboard.isVisible()}
                >
                    <DialogBody
                        style={[styles.dialog, { backgroundColor: background }]}
                        title={PROFILES_ADD_NEW_PROFILE_TITLE}
                        titleTestID={PROFILE_SELECTION_TITLE}
                        DescriptionComponent={renderDescriptionComponent}
                        actionButtonTitle={BUTTON_SAVE}
                        actionButtonFillColor={primary}
                        actionButtonStyle={[styles.button, buttonStyle]}
                        actionButtonTextStyle={styles.buttonText}
                        actionButtonTestID={ADD_PROFILE_SAVE_BUTTON}
                        handleActionButtonPress={handleActionButtonPress}
                        actionButtonBordered
                    />
                    <SvgButton
                        Svg={CloseIcon}
                        style={styles.closeButton}
                        svgStyle={styles.closeSvg}
                        onPress={handleClose}
                        testID={PROFILE_CLOSE_BUTTON}
                        accessibilityLabel={t(ACCESSIBILITY_CLOSE_BUTTON)}
                        accessibilityRole={Platform.select({
                            web: 'imagebutton',
                            default: 'button',
                        })}
                        accessible
                    />

                    {isPhone && screenReaderEnabled && (
                        <BottomSheetAccessibleList
                            visible={isBottomSheetOpened}
                            data={data}
                            renderRowItem={renderBottomSheetRow}
                            keyExtractor={keyExtractor}
                            onClose={handleCloseBottomSheet}
                            headerTitle={t(
                                isChildProfile
                                    ? PROFILE_KIDS_AGE_BUTTON
                                    : PROFILE_AGE_RATING_BUTTON,
                            )}
                        />
                    )}
                    {isPhone && !screenReaderEnabled && (
                        <BottomSheetList
                            ref={bottomSheetRef}
                            data={data}
                            renderRowItem={renderBottomSheetRow}
                            keyExtractor={keyExtractor}
                            rowHeight={rowStyles.container.height}
                            onClose={handleCloseBottomSheet}
                        />
                    )}

                    <ErrorDialog
                        visible={isInternetConnected && showErrorDialog}
                        animationType="fade"
                        title={errorContent.title}
                        description={errorContent.description}
                        titleTestID={errorContent.titleTestID}
                        descriptionTestID={errorContent.descriptionTestID}
                        errorCode={`${addProfileErrorCode || ''}`}
                        errorCodeTestID={PROFILE_ADD_PROFILE_ERROR_CODE}
                        actionButtonTitle={errorContent.actionButtonText}
                        actionButtonTestID={errorContent.actionButtonTestID}
                        actionButtonOnPress={handleHideErrorDialog}
                        cancelButtonTitle={errorContent.cancelButtonText}
                        closeButtonTestID={errorContent.cancelButtonTestID}
                        cancelButtonFillColor={primary}
                        cancelButtonStyle={[styles.button, buttonStyle]}
                        actionButtonStyle={styles.button}
                        cancelButtonTextStyle={styles.buttonText}
                        cancelButtonOnPress={handleCloseErrorDialog}
                        errorStatus={addProfileErrorStatus}
                        actionButtonBordered
                        revertButtons
                    />
                </KeyboardAwareScrollView>
            </ScrollView>
        </SafeAreaView>
    );
}

export default AddProfilePage;

function keyExtractor({
    key,
}: {
    label: string;
    key: ParentalControlRating | string;
    isSelected: boolean;
}) {
    return key;
}
